import { useCallback, useEffect, useRef, useState } from "react";
import { SB } from "./tokens";

export type PendingDelete = {
  id: number;
  text: string;
  /** Sends the real delete. Runs when the undo window closes. */
  commit: () => void;
  /** Puts the optimistically hidden item back. */
  restore: () => void;
};

/**
 * Delete-with-undo for the scenario bar popovers: the caller hides the item at
 * once (usually by editing the query cache), a toast offers Undo for six
 * seconds, and the real delete request is sent only when the window closes.
 * Starting a second delete, dismissing the toast or unmounting commits any
 * pending one first, so nothing is silently lost.
 */
export function useUndoableDelete() {
  const [pending, setPending] = useState<PendingDelete | null>(null);
  const pendingRef = useRef<PendingDelete | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const commitPending = useCallback(() => {
    clearTimer();
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    if (current) current.commit();
  }, []);

  const scheduleDelete = useCallback(
    (item: Omit<PendingDelete, "id">) => {
      // Only one undo window at a time: the previous delete goes through.
      commitPending();
      const next = { ...item, id: Date.now() };
      pendingRef.current = next;
      setPending(next);
      timerRef.current = setTimeout(commitPending, SB.undoMs);
    },
    [commitPending]
  );

  const undo = useCallback(() => {
    clearTimer();
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    if (current) current.restore();
  }, []);

  // Leaving the page (or the strategy) must not swallow a delete the user
  // believes has happened.
  useEffect(() => () => commitPending(), [commitPending]);

  return { pending, scheduleDelete, undo, dismiss: commitPending };
}
